import { datePl, type MeDto, type MyFleetDto, type MyVehicleDto, REPORT_CATEGORIES, UNAVAILABILITY_REASONS } from '@bqcrm/shared';
import { Badge, Button, Card, Group, Modal, NumberInput, SimpleGrid, Stack, Table, Text, ThemeIcon } from '@mantine/core';
import { IconAlertTriangle, IconCar, IconGauge } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../../api';
import { BlocksUseBadge, FleetReportStatusBadge } from '../../components/Badges';
import { PageHeader } from '../../components/PageHeader';
import { QueryState } from '../../components/QueryState';
import { EmptyState, SectionCard } from '../../components/ui';
import { formatDateTime } from '../../format';
import { notifyError, notifyOk } from '../../notify';
import { kmPl, ReportFormModal } from './common';

/** „Mój samochód” — dane z /fleet/my (użytkownik przypisanego pojazdu, także bez modułu Flota). */
export const useMyFleet = (me: MeDto) => useQuery({ queryKey: ['fleet', 'my'], queryFn: () => api<MyFleetDto>('/fleet/my'), enabled: me.features.flota, staleTime: 60_000, retry: false });

function OdometerModal({ vehicle, onClose }: { vehicle: MyVehicleDto; onClose: () => void }) {
  const qc = useQueryClient();
  const [km, setKm] = useState<number | null>(null);
  const save = useMutation({
    mutationFn: () => api('/fleet/my/odometer', { method: 'POST', body: { vehicleId: vehicle.id, km } }),
    onSuccess: () => {
      notifyOk('Zapisano przebieg');
      void qc.invalidateQueries({ queryKey: ['fleet'] });
      onClose();
    },
    onError: notifyError,
  });
  return (
    <Modal opened onClose={onClose} title={`Przebieg: ${vehicle.plate}`}>
      <Stack gap="sm">
        {vehicle.odometer && (
          <Text size="sm" c="dimmed">
            Ostatni znany: {kmPl(vehicle.odometer.km)} ({datePl(vehicle.odometer.readAt)})
          </Text>
        )}
        <NumberInput label="Stan licznika teraz" required suffix=" km" min={0} max={5_000_000} thousandSeparator=" " allowDecimal={false} value={km ?? ''} onChange={(x) => setKm(typeof x === 'number' ? x : null)} data-autofocus />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Anuluj
          </Button>
          <Button disabled={km === null} loading={save.isPending} onClick={() => save.mutate()}>
            Zapisz przebieg
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/** Mój samochód: przypisane auta, zgłoszenie problemu, podanie przebiegu i moje zgłoszenia (bez kosztów i dokumentów). */
export function MyVehiclePage({ me }: { me: MeDto }) {
  const q = useMyFleet(me);
  const [reportFor, setReportFor] = useState<string | null | undefined>(undefined);
  const [odo, setOdo] = useState<MyVehicleDto | null>(null);
  const vehicles = q.data?.vehicles ?? [];
  return (
    <>
      <PageHeader
        title="Mój samochód"
        description="Auto przypisane do Ciebie lub Twojej ekipy: zgłoś usterkę albo potrzebę i podaj przebieg. Terminy serwisów pilnuje zarządzający flotą."
        actions={
          vehicles.length > 0 && (
            <Button leftSection={<IconAlertTriangle size={16} />} onClick={() => setReportFor(null)}>
              Zgłoś problem lub potrzebę
            </Button>
          )
        }
      />
      <QueryState isLoading={q.isLoading} error={q.error}>
        {!vehicles.length ? (
          <Card>
            <EmptyState icon={<IconCar size={24} />} title="Nie masz przypisanego samochodu" hint="Gdy zarządzający flotą przypisze Ci auto (albo Twojej ekipie), pojawi się tutaj." />
          </Card>
        ) : (
          <Stack gap="md">
            <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
              {vehicles.map((v) => (
                <Card key={v.id} padding="lg">
                  <Group justify="space-between" align="flex-start" wrap="nowrap">
                    <Group gap="sm" wrap="nowrap">
                      <ThemeIcon size={42} radius="md" variant="light">
                        <IconCar size={22} />
                      </ThemeIcon>
                      <div>
                        <Text fw={800} size="lg">
                          {v.plate}
                        </Text>
                        <Text size="sm" c="dimmed">
                          {[v.make, v.model].filter(Boolean).join(' ') || '—'} · {v.companyName}
                        </Text>
                      </div>
                    </Group>
                    {v.unavailable ? (
                      <Badge color="red" variant="light">
                        Niedostępny do {datePl(v.unavailable.dateTo)}
                      </Badge>
                    ) : (
                      <Badge color="green" variant="light">
                        Dostępny
                      </Badge>
                    )}
                  </Group>
                  <Stack gap={4} mt="md">
                    <Text size="sm">
                      <Text span c="dimmed">
                        Przypisanie:{' '}
                      </Text>
                      {v.holderLabel}
                    </Text>
                    <Text size="sm">
                      <Text span c="dimmed">
                        Odpowiedzialny:{' '}
                      </Text>
                      {v.iAmResponsible ? 'Ty' : v.responsibleName}
                    </Text>
                    <Text size="sm">
                      <Text span c="dimmed">
                        Ostatni przebieg:{' '}
                      </Text>
                      {v.odometer ? `${kmPl(v.odometer.km)} (${datePl(v.odometer.readAt)})` : 'brak odczytu'}
                    </Text>
                    {v.unavailable && (
                      <Text size="sm" c="var(--app-text-red)">
                        {UNAVAILABILITY_REASONS[v.unavailable.reason]} — do {datePl(v.unavailable.dateTo)}
                      </Text>
                    )}
                  </Stack>
                  <Group gap="xs" mt="md">
                    <Button size="xs" leftSection={<IconAlertTriangle size={14} />} onClick={() => setReportFor(v.id)}>
                      Zgłoś problem
                    </Button>
                    <Button size="xs" variant="light" leftSection={<IconGauge size={14} />} onClick={() => setOdo(v)}>
                      Podaj przebieg
                    </Button>
                  </Group>
                </Card>
              ))}
            </SimpleGrid>
            <SectionCard title="Moje zgłoszenia" padding={0}>
              {q.data?.reports.length ? (
                <Table.ScrollContainer minWidth={600}>
                  <Table highlightOnHover>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Numer</Table.Th>
                        <Table.Th>Pojazd</Table.Th>
                        <Table.Th>Opis</Table.Th>
                        <Table.Th>Status</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {q.data.reports.map((r) => (
                        <Table.Tr key={r.id}>
                          <Table.Td>
                            <Text size="sm" fw={600}>
                              {r.number}
                            </Text>
                            <Text size="xs" c="dimmed">
                              {formatDateTime(r.createdAt)}
                            </Text>
                          </Table.Td>
                          <Table.Td>{r.plate}</Table.Td>
                          <Table.Td>
                            <Text size="sm" lineClamp={2}>
                              {REPORT_CATEGORIES[r.category]}: {r.description}
                            </Text>
                            {r.statusNote && (
                              <Text size="xs" c="dimmed">
                                {r.statusNote}
                              </Text>
                            )}
                          </Table.Td>
                          <Table.Td>
                            <Group gap={4}>
                              {r.blocksUse && <BlocksUseBadge size="xs" />}
                              <FleetReportStatusBadge status={r.status} />
                            </Group>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </Table.ScrollContainer>
              ) : (
                <EmptyState compact title="Nie masz jeszcze zgłoszeń" />
              )}
            </SectionCard>
          </Stack>
        )}
      </QueryState>
      {reportFor !== undefined && <ReportFormModal vehicles={vehicles.map((v) => ({ id: v.id, label: `${v.plate} ${[v.make, v.model].filter(Boolean).join(' ')}`.trim() }))} initialVehicleId={reportFor} onClose={() => setReportFor(undefined)} />}
      {odo && <OdometerModal vehicle={odo} onClose={() => setOdo(null)} />}
    </>
  );
}
